/**
 * V2.0.47 — Opening code minimums (P1 Phase 4 + ADA Mode).
 *
 * Pure constants module — no React, no DOM. Used by `wallAdjacency`
 * (drag clamps + auto-shrink) and `LiveInspectorRail` (ADA toggle).
 *
 * IRC residential minimums (used when ADA Mode is OFF):
 *   - door:   0.76 m / 30″
 *   - window: 0.61 m / 24″ (egress requires 24″ × 24″ clear)
 *
 * ADA-aligned minimums (used when ADA Mode is ON, per ICC A117.1):
 *   - door:   0.813 m / 32″ clear
 *   - primary bedroom + bathroom door bumped to 0.914 m / 36″
 *   - hallway clear: 0.914 m / 36″
 *   - window sill (visual advisory only): max 1.219 m / 48″ AFF
 *
 * Edge margin: 0.20 m total (10 cm of jamb each side) is the smallest
 * gap we permit between an opening and the nearest wall corner.
 */

export const EDGE_MARGIN_M = 0.2;

export const IRC_DOOR_MIN_M = 0.76;
export const IRC_WINDOW_MIN_M = 0.61;

export const ADA_DOOR_MIN_M = 0.813;          // 32″ clear
export const ADA_PRIMARY_DOOR_MIN_M = 0.914;  // 36″ for primary bedroom + bathroom
export const ADA_HALLWAY_MIN_M = 0.914;       // 36″ clear hallway
export const ADA_WINDOW_SILL_MAX_M = 1.219;   // 48″ AFF advisory

// Room name → ADA preferred-width tier. Match is case-insensitive +
// substring-based so "Primary Bedroom", "Master Bath", "Powder Room"
// all flow through.
const ADA_PRIMARY_ROOM_KEYWORDS = [
  "primary",
  "master",
  "bath",
  "wc",
  "powder",
  "accessible",
];

export function adaDoorTargetWidth(roomName) {
  const n = String(roomName || "").toLowerCase();
  if (ADA_PRIMARY_ROOM_KEYWORDS.some((kw) => n.includes(kw))) {
    return ADA_PRIMARY_DOOR_MIN_M;
  }
  return ADA_DOOR_MIN_M;
}

export function minOpeningWidth(kind, { adaMode = false } = {}) {
  if (kind === "door") return adaMode ? ADA_DOOR_MIN_M : IRC_DOOR_MIN_M;
  return adaMode ? IRC_WINDOW_MIN_M : IRC_WINDOW_MIN_M; // windows unchanged by ADA toggle
}

export function clampOpeningWidth(kind, currentWidth, hostWallLength, opts = {}) {
  // Available width = wallLength - 2 jambs. We never allow below the
  // applicable code minimum. Returns { width, blocked }.
  const min = minOpeningWidth(kind, opts);
  const available = hostWallLength - EDGE_MARGIN_M;
  if (available < min) return { width: currentWidth, blocked: true, min };
  return { width: Math.min(currentWidth, available), blocked: false, min };
}
